import { memo, MouseEvent as ReactMouseEvent } from "react"

import { CellProps, useFocusManagement } from "components/TreeState"

import { Bulky } from "./Bulky"
import { CellError } from "./CellError"
import { ChangedPlanRecord } from "../types"
import DownIcon from "@material-ui/icons/KeyboardArrowDown"
import { Exclusion } from "models/packages"
import { FieldsKey } from "../components/AutoCompute/types"
import Input from "ui/inputs/Input"
import { css } from "@emotion/css"
import { get } from "lodash"
import { isSemanticReadonly } from "../utils"
import { useCellDialog } from "../useCellDialog"
import { useI18n } from "providers/I18n"
import { useSnapshot } from "valtio"
import { useTableSource } from "../TableStateProvider"

export const ExcludedHoursCell = memo(function _ExcludedHoursCell(
  props: CellProps,
) {
  const { t } = useI18n()
  const { rowId: id, dataKey, columnId, disabled, primaryKey, semantic } = props
  const { onChange } = useCellDialog()
  const tableSource = useTableSource()
  const readFrom = useSnapshot(tableSource.state)
  const readError = useSnapshot(tableSource.errors)

  const error = get(readError, `${id}.${columnId}`)
  const row = get(readFrom, `${id}`) as ChangedPlanRecord
  const type = get(row, "type")
  const packaged = get(row, "packaged")

  const readonly = isSemanticReadonly(
    type,
    primaryKey as FieldsKey,
    semantic,
    packaged,
  )

  const exclusions = (get(row, `${dataKey}.exclusions`) || []) as Exclusion[]
  const days = exclusions?.map((e) => e.day)
  const svalue = days?.map((d) => t(`days.${d}`)).join(",")

  const { getInputProps } = useFocusManagement()
  const { onBlur } = getInputProps()

  const onOpenExclusions = (
    evt: ReactMouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    const anchor = evt.currentTarget
    onChange({ type: "exclusions", fieldInfo: { rowId: id, dataKey }, anchor })
  }

  return readonly ? (
    <span />
  ) : (
    <>
      <Bulky {...props}>
        <div
          className={styles.primaryInput}
          onPointerDown={(evt) => !disabled && onOpenExclusions(evt)}
        >
          <Input
            readOnly
            onBlur={onBlur}
            value={svalue}
            style={{ textAlign: "end" }}
            autoComplete="off"
            type="tel"
          />
          <DownIcon />
        </div>
      </Bulky>
      {error ? <CellError error={error} /> : null}
    </>
  )
})

const styles = {
  icon: css`
    --btn-padding: var(--spacing-xxs);
    --btn-min-width: var(--spacing-xxl-2-1);
    &:focus {
      box-shadow: none;
    }
  `,
  primaryInput: css`
    display: flex;
    flex-direction: row;
    width: 100%;
    font-size: 13px;
  `,
}
